import { useEffect, useState } from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react/hooks";
import { wordpressClient } from "../../external/wordpress/wordpressClient";
import Meta from "../../components/custom-ui/meta/Meta";
import { useAuth } from "../../providers/auth";
import { Tab } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import GeneralInfoPostDisplay_NewUI from "../../components/general-info/GeneralInfoPostaDisplay_NewUI";
import GeneralInfoCategoryPost_NewUI from "./GneralInfo_forEmployer";

const seekerFaqList = [
    {
        question: "Finding Your Next Opportunity",
        answer:
            'You can browse all of our current openings by visiting the "Jobs" tab right here in the app. You\'ll find positions with Instant Teams and our employer partners. You can easily distinguish the employer by the logo shown:<br/><br/><b>Instant Teams Logo:</b> Roles within our customer teams.<br/><b>Partner Logo:</b> Roles hired directly by our partner employers.<br/><br/>We are constantly growing, so we recommend checking back frequently—new roles are added on a regular basis!',
    },
    {
        question: "Available Roles at Instant Teams",
        answer:
            "We provide a variety of W2 employment opportunities tailored to different schedules and needs, including:<br/><br/><b>Full-Time:</b> Standard 40-hour work weeks with consistent schedules.<br/><b>Part-Time:</b> For those seeking a lighter workload.<br/><b>Temporary &amp; Seasonal:</b> Short-term contracts or peak-season roles.<br/><br/>Specific details regarding hours, responsibilities, and benefits are outlined in each individual job description.",
    },
    {
        question: "Job Posting Duration",
        answer:
            "Our job postings remain active until the position is successfully filled.<br/><br/>Because some roles move very quickly, we highly recommend enabling <b>push notifications</b> so you're alerted the moment a new opportunity goes live!",
    },
    {
        question: "New Job Postings",
        answer:
            "New opportunities are added daily! We are constantly updating our listings with fresh remote roles from Instant Teams and our partners.<br/><br/>If you don't see a position that matches your current skill set, we recommend enabling <b>push notifications</b> in the app. This ensures you are the first to know when a role that fits your background becomes available.",
    },
    {
        question: "Eligibility for Non-Military Applicants",
        answer:
            "Absolutely! While we are proud to support the military community, our platform is open to everyone.<br/><br/>Anyone can create an account and apply for positions with Instant Teams or our partner employers. We value talent from all backgrounds!",
    },
    {
        question: "OCONUS Opportunities",
        answer:
            "We occasionally offer OCONUS-friendly positions! Please keep the following in mind:<br/><br/><b>Requirements:</b> Any specific location constraints or legal requirements will be clearly listed in the job description.<br/><br/>Be sure to review the details of each posting carefully to ensure the role aligns with your current location.",
    },
    {
        question: "Why We Request Your Physical Address",
        answer:
            "We collect your physical address for two primary reasons:<br/><br/><b>Identity Verification:</b> It helps us confirm you are a real applicant and ensures the security of our platform.<br/><b>Eligibility Check:</b> Many roles have specific geographic or residency requirements. Knowing your location allows us to match you with the positions you are legally eligible to fill.",
    },
    {
        question: "Accessing Your Account",
        answer:
            'You can log in to your Instant Teams account anytime via the web or on your mobile device:<br/><br/><b>Web:</b> Visit <a href="https://jobs.instantteams.com">jobs.instantteams.com</a>.<br/><b>Mobile App:</b> Download the FREE Instant Teams Job app, available now in both the Apple App Store and the Google Play Store.',
    },
];

function GeneralInfoPage_NewUI() {
    const usertype = useAuth()?.user?.claims?.type;
    const [nodes, setNodes] = useState();
    const navigate = useNavigate();

    const parent = usertype === "A" ? 6 : usertype === "E" ? 7 : 5;
    const parentInt = Number.isInteger(parent)
        ? parent
        : Math.floor(parseFloat(parent));

    const getNodes = gql`
    query nodeQuery($parent: Int!) {
      categories(where: { parent: $parent }) {
        edges {
          node {
            id
            name
            posts {
              edges {
                node {
                  id
                }
              }
            }
          }
        }
      }
    }
  `;

    const { loading, error, data } = useQuery(getNodes, {
        variables: { parent: parentInt },
        client: wordpressClient,
        fetchPolicy: "cache-and-network",
        skip: usertype !== "A",
    });

    useEffect(() => {
        setNodes(data?.categories?.edges);
    }, [data]);

    const isEmployer = usertype === "E";
    const isAdmin = usertype === "A";
    const isSeeker = !isEmployer && !isAdmin;

    const heading = isEmployer ? "Employer" : "Seeker";
    const subtitle = isEmployer
        ? "Everything you need to know about hiring through Instant Teams"
        : "Everything you need to know about finding your next role";

    return (
        <div className="min-h-screen bg-gradient-to-b pt-24 from-slate-50 to-white">
            <Meta title="Frequently Asked Questions" />

            {/* Seeker & Employer view */}
            {(isSeeker || isEmployer) && (
                <div className="max-w-3xl mx-auto px-6 xs:px-4 md:px-6 py-12 xs:py-8">
                    {/* Header */}
                    <div className="mb-10 xs:mb-6">
                        {/* <div className="inline-block mb-3 px-3 py-1 rounded-full bg-instant-teams-teal-D2 text-instant-teams-teal-Main text-xs font-semibold tracking-wide uppercase">
                            {heading}
                        </div> */}
                        <h1 className="text-3xl xs:text-2xl pt-8 font-bold text-instant-teams-blue-Main leading-tight">
                            Frequently Asked Questions
                        </h1>
                        <p className="mt-2 text-gray-500 text-[15px] leading-relaxed">
                            {subtitle}
                        </p>
                    </div>

                    {/* FAQ Accordion */}
                    <GeneralInfoCategoryPost_NewUI
                        faqList={isSeeker ? seekerFaqList : undefined}
                    />
                </div>
            )}

            {/* Admin view (WordPress-based) */}
            {isAdmin && nodes?.length > 0 ? (
                <div className="max-w-3xl mx-auto px-6 xs:px-4 md:px-6 py-12 xs:py-8">
                    <div className="mb-10 xs:mb-6">
                        <h1 className="text-3xl xs:text-2xl font-bold text-instant-teams-blue-Main leading-tight">
                            Frequently Asked Questions
                        </h1>
                    </div>

                    <Tab.Group>
                        <Tab.Panels className="w-full">
                            {nodes.map(({ node }) => {
                                if (node?.posts?.edges?.length > 0) {
                                    return (
                                        <Tab.Panel
                                            key={node?.id}
                                            className="flex flex-col"
                                        >
                                            <GeneralInfoPostDisplay_NewUI
                                                posts={node?.posts?.edges}
                                                key={node?.id}
                                            />
                                        </Tab.Panel>
                                    );
                                }
                                return (
                                    <Tab.Panel key={node?.id}>
                                        <p className="text-gray-400 text-center py-8">No data available</p>
                                    </Tab.Panel>
                                );
                            })}
                        </Tab.Panels>
                    </Tab.Group>
                </div>
            ) : isAdmin && (
                <div className="w-full mt-28 flex h-full justify-center items-center">
                    <img src="/images/loader_v2.gif" alt="loader" />
                </div>
            )}
        </div>
    );
}

export default GeneralInfoPage_NewUI;
